import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { attractionAPI, adminAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import { PageHeader } from '../components/ui';
import './ModuleAdmin.css';
import './AttractionForm.css';

const emptyForm = {
  name: '',
  slug: '',
  subtitle: '',
  brief: '',
  description: '',
  imageUrl: '',
  tagsText: '',
  galleryText: ''
};

const emptyFaq = () => ({ q: '', a: '' });

/** Material icon names used on the public attraction page — plain-English labels */
const UNIQUE_ICON_CHOICES = [
  { value: 'landscape', label: 'Mountains / landscape' },
  { value: 'schedule', label: 'Time / calendar' },
  { value: 'temple_hindu', label: 'Temple / culture' },
  { value: 'groups', label: 'People / community' },
  { value: 'restaurant_menu', label: 'Food / dining' },
  { value: 'hiking', label: 'Hiking / trekking' },
  { value: 'place', label: 'Place (generic)' },
  { value: 'wb_sunny', label: 'Weather / sun' },
  { value: 'photo_camera', label: 'Photo / gallery' },
  { value: 'map', label: 'Map / location' }
];

const emptyHighlight = () => ({ label: '', value: '', icon: 'landscape' });
const emptyThing = () => ({ title: '', description: '', icon: 'temple_hindu', url: '' });

const defaultHighlightRows = () => [
  emptyHighlight(),
  { label: '', value: '', icon: 'schedule' },
  { label: '', value: '', icon: 'temple_hindu' }
];

const blockStyle = {
  display: 'grid',
  gap: 8,
  marginBottom: 12,
  padding: 12,
  border: '1px solid #e5e7eb',
  borderRadius: 8
};

function IconPicker({ value, onChange }) {
  const v = value || 'landscape';
  return (
    <div>
      <div className="attraction-icon-picker" role="group" aria-label="Choose icon">
        {UNIQUE_ICON_CHOICES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`attraction-icon-picker-btn${v === opt.value ? ' is-selected' : ''}`}
            title={opt.label}
            aria-label={opt.label}
            aria-pressed={v === opt.value}
            onClick={() => onChange(opt.value)}
          >
            <span className="material-symbols-outlined attraction-form-ms">{opt.value}</span>
          </button>
        ))}
      </div>
      <p className="attraction-icon-picker-hint">Hover a symbol for its name. Selected icon is highlighted.</p>
    </div>
  );
}

const AttractionForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const { showToast } = useToast();

  const { data: itemRes } = useQuery(
    ['attraction', id],
    () => attractionAPI.get(id),
    { enabled: !!isEdit && !!id }
  );
  const existing = itemRes?.data?.data;

  const { data: treksRes } = useQuery(['attraction-form-treks'], () => trekAPI.list({ limit: 500, page: 1 }), {
    staleTime: 60_000
  });
  const trekRows = treksRes?.data?.data || [];
  const treksSorted = [...trekRows].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
  );

  const [form, setForm] = useState(emptyForm);
  const [faqs, setFaqs] = useState([emptyFaq()]);
  const [highlightRows, setHighlightRows] = useState(defaultHighlightRows);
  const [thingRows, setThingRows] = useState([emptyThing()]);
  /** Ordered trek ids (strings) for “Nearby treks” */
  const [relatedTreks, setRelatedTreks] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  useEffect(() => {
    if (existing) {
      const tags = Array.isArray(existing.tags) ? existing.tags.join('\n') : '';
      const gallery = Array.isArray(existing.galleryUrls) ? existing.galleryUrls.join('\n') : '';
      let faqRows = [emptyFaq()];
      if (Array.isArray(existing.faqs) && existing.faqs.length) {
        faqRows = existing.faqs.map((f) => ({
          q: f.q || f.question || '',
          a: f.a || f.answer || ''
        }));
      }
      setForm({
        name: existing.name ?? '',
        slug: existing.slug ?? '',
        subtitle: existing.subtitle ?? '',
        brief: existing.brief ?? '',
        description: existing.description ?? '',
        imageUrl: existing.imageUrl ?? '',
        tagsText: tags,
        galleryText: gallery
      });
      setFaqs(faqRows);

      if (Array.isArray(existing.highlights) && existing.highlights.length) {
        setHighlightRows(
          existing.highlights.map((h) => ({
            label: h.label || '',
            value: h.value || '',
            icon: h.icon || 'landscape'
          }))
        );
      } else {
        setHighlightRows(defaultHighlightRows());
      }

      if (Array.isArray(existing.thingsToDo) && existing.thingsToDo.length) {
        setThingRows(
          existing.thingsToDo.map((t) => ({
            title: t.title || '',
            description: t.description || t.body || '',
            icon: t.icon || 'place',
            url: t.url || ''
          }))
        );
      } else {
        setThingRows([emptyThing()]);
      }

      setRelatedTreks(
        Array.isArray(existing.relatedTrekIds) && existing.relatedTrekIds.length
          ? existing.relatedTrekIds.map(String)
          : []
      );
    }
  }, [existing]);

  const mutation = useMutation(
    (payload) => (isEdit ? attractionAPI.update(id, payload) : attractionAPI.create(payload)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['attractions']);
        navigate(-1);
        showToast({
          message: isEdit ? 'Attraction updated successfully.' : 'Attraction created successfully.',
          severity: 'success'
        });
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Save failed. Check that the API is reachable and the database migration for attractions has been run.';
        showToast({ message: msg, severity: 'error' });
      }
    }
  );

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url || res.data?.url;
      if (url) setForm((p) => ({ ...p, imageUrl: url }));
    } catch (err) {
      showToast({
        message: err?.response?.data?.message || 'Image upload failed.',
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setGalleryUploading(true);
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url || res.data?.url;
      if (url) {
        setForm((p) => {
          const cur = (p.galleryText || '').trim();
          const next = cur ? `${cur}\n${url}` : url;
          return { ...p, galleryText: next };
        });
      }
    } catch (err) {
      showToast({
        message: err?.response?.data?.message || 'Gallery upload failed.',
        severity: 'error'
      });
    } finally {
      setGalleryUploading(false);
      e.target.value = '';
    }
  };

  const buildHighlightsPayload = () => {
    const out = highlightRows
      .map((h) => ({
        label: (h.label || '').trim(),
        value: (h.value || '').trim(),
        icon: (h.icon || 'landscape').trim() || 'landscape'
      }))
      .filter((h) => h.label && h.value);
    return out.length ? out : null;
  };

  const buildThingsPayload = () => {
    const out = thingRows
      .map((t) => ({
        title: (t.title || '').trim(),
        description: (t.description || '').trim(),
        icon: (t.icon || 'place').trim() || 'place',
        url: (t.url || '').trim() || null
      }))
      .filter((t) => t.title && t.description)
      .map((t) => (t.url ? t : { title: t.title, description: t.description, icon: t.icon }));
    return out.length ? out : null;
  };

  const buildRelatedTrekIdsPayload = () => {
    const seen = new Set();
    const ordered = [];
    for (const slot of relatedTreks) {
      const n = Number(String(slot).trim());
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
      seen.add(n);
      ordered.push(n);
    }
    return ordered.length ? ordered : null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      showToast({
        message: 'Name is required.',
        severity: 'warning'
      });
      return;
    }
    const faqPayload = faqs
      .map((f) => ({ q: (f.q || '').trim(), a: (f.a || '').trim() }))
      .filter((f) => f.q && f.a);
    const payload = {
      name: form.name.trim(),
      slug: (form.slug || '').trim() || undefined,
      subtitle: (form.subtitle || '').trim() || null,
      brief: (form.brief || '').trim() || null,
      description: (form.description || '').trim() || null,
      imageUrl: (form.imageUrl || '').trim() || null,
      tags: (form.tagsText || '').trim() || null,
      galleryUrls: (form.galleryText || '').trim() || null,
      faqs: faqPayload.length ? faqPayload : null,
      highlights: buildHighlightsPayload(),
      thingsToDo: buildThingsPayload(),
      relatedTrekIds: buildRelatedTrekIdsPayload()
    };
    mutation.mutate(payload);
  };

  const updateHighlight = (idx, patch) => {
    setHighlightRows((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const removeHighlight = (idx) => {
    setHighlightRows((rows) => rows.filter((_, i) => i !== idx));
  };

  const updateThing = (idx, patch) => {
    setThingRows((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const removeThing = (idx) => {
    setThingRows((rows) => (rows.length <= 1 ? [emptyThing()] : rows.filter((_, i) => i !== idx)));
  };

  const moveRelatedTrek = (idx, dir) => {
    setRelatedTreks((rows) => {
      const j = idx + dir;
      if (j < 0 || j >= rows.length) return rows;
      const next = [...rows];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  return (
    <div className="module-page">
      <AdminBackLink to="/attractions" label="Back to attractions" />
      <PageHeader
        title={isEdit ? 'Edit Attraction' : 'Add Attraction'}
        subtitle="Content here powers the public attraction page (opened from event detail). Leave URL slug blank to auto-generate from the name."
      />

      <div className="module-card">
        <form onSubmit={handleSubmit}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Attraction name (must match trek &quot;Attractions&quot; row)"
                required
              />
            </div>
            <div>
              <label>URL slug</label>
              <input
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                placeholder="e.g. sankri-village (optional)"
              />
            </div>
            <div>
              <label>Tagline (shown under the big title)</label>
              <input
                value={form.subtitle}
                onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                placeholder="e.g. The gateway to the Himalayas"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Brief</label>
              <input
                value={form.brief}
                onChange={(e) => setForm((p) => ({ ...p, brief: e.target.value }))}
                placeholder="One-line summary (also used as tagline if subtitle is empty)"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Tags (one per line)</label>
              <textarea
                rows={4}
                value={form.tagsText}
                onChange={(e) => setForm((p) => ({ ...p, tagsText: e.target.value }))}
                placeholder={'Winter special\nHigh altitude\nScenic views'}
              />
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 6 }}>
                Optional: if the highlight rows below are empty, the site uses tags as stat cards. Put{' '}
                <strong>label and value on one line</strong>, e.g. <code>Altitude: 6,400 ft</code>. If you split across
                lines (e.g. <code>Altitude: 6</code> then <code>400 ft</code>), the page will merge them into one value.
              </p>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label>Highlight stat cards (up to 6)</label>
              <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 8px' }}>
                Shown as three boxes on the public page (altitude, best time, etc.). Each row needs both{' '}
                <strong>label</strong> and <strong>value</strong> filled, or that row is skipped when you save.
              </p>
              {highlightRows.map((row, idx) => (
                <div key={`h-${idx}`} style={blockStyle}>
                  <input
                    value={row.label}
                    onChange={(e) => updateHighlight(idx, { label: e.target.value })}
                    placeholder="e.g. Altitude"
                  />
                  <input
                    value={row.value}
                    onChange={(e) => updateHighlight(idx, { value: e.target.value })}
                    placeholder="e.g. 6,400 ft"
                  />
                  <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Icon</label>
                  <IconPicker value={row.icon} onChange={(v) => updateHighlight(idx, { icon: v })} />
                  <div>
                    <button type="button" className="module-link-btn danger" onClick={() => removeHighlight(idx)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="module-btn secondary"
                disabled={highlightRows.length >= 6}
                onClick={() => setHighlightRows((r) => [...r, emptyHighlight()])}
              >
                Add highlight
              </button>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label>Things to do</label>
              <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 8px' }}>
                Short cards on the public page. Link is optional (opens in a new tab if it starts with http).
              </p>
              {thingRows.map((row, idx) => (
                <div key={`t-${idx}`} style={blockStyle}>
                  <input
                    value={row.title}
                    onChange={(e) => updateThing(idx, { title: e.target.value })}
                    placeholder="Title"
                  />
                  <textarea
                    rows={3}
                    value={row.description}
                    onChange={(e) => updateThing(idx, { description: e.target.value })}
                    placeholder="Short description"
                  />
                  <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Icon</label>
                  <IconPicker value={row.icon} onChange={(v) => updateThing(idx, { icon: v })} />
                  <input
                    value={row.url}
                    onChange={(e) => updateThing(idx, { url: e.target.value })}
                    placeholder="Optional link (https://…)"
                  />
                  <div>
                    <button type="button" className="module-link-btn danger" onClick={() => removeThing(idx)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" className="module-btn secondary" onClick={() => setThingRows((r) => [...r, emptyThing()])}>
                Add activity
              </button>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label>Nearby treks</label>
              <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 8px' }}>
                Choose treks by name. Order here matches the horizontal list on the public page. Treks come from Admin →
                Treks (up to 500 loaded).
              </p>
              {relatedTreks.map((trekId, idx) => (
                <div
                  key={`rel-${idx}-${trekId}`}
                  style={{ ...blockStyle, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
                >
                  <select
                    value={String(trekId)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRelatedTreks((rows) => {
                        const next = [...rows];
                        next[idx] = v;
                        return next;
                      });
                    }}
                    style={{ flex: '1 1 220px', minWidth: 200 }}
                  >
                    {treksSorted.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name} (id {t.id})
                      </option>
                    ))}
                  </select>
                  <button type="button" className="module-btn secondary" onClick={() => moveRelatedTrek(idx, -1)} disabled={idx === 0}>
                    Up
                  </button>
                  <button
                    type="button"
                    className="module-btn secondary"
                    onClick={() => moveRelatedTrek(idx, 1)}
                    disabled={idx >= relatedTreks.length - 1}
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    className="module-link-btn danger"
                    onClick={() => setRelatedTreks((rows) => rows.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div style={{ marginTop: relatedTreks.length ? 8 : 0 }}>
                <select
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    if (relatedTreks.includes(v)) {
                      showToast({ message: 'That trek is already in the list.', severity: 'warning' });
                      e.target.value = '';
                      return;
                    }
                    setRelatedTreks((rows) => [...rows, v]);
                    e.target.value = '';
                  }}
                >
                  <option value="">{relatedTreks.length ? '+ Add another trek…' : '+ Add a trek…'}</option>
                  {treksSorted.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name} (id {t.id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea
                rows={8}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Full description (HTML allowed)"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Hero image</label>
              <div className="attraction-form-image">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="module-btn secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading…' : 'Upload'}
                </button>
                {form.imageUrl && (
                  <div className="attraction-form-image-preview">
                    <img src={form.imageUrl} alt="Attraction" />
                    <button
                      type="button"
                      className="module-link-btn danger"
                      onClick={() => setForm((p) => ({ ...p, imageUrl: '' }))}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Gallery images (one URL per line)</label>
              <textarea
                rows={5}
                value={form.galleryText}
                onChange={(e) => setForm((p) => ({ ...p, galleryText: e.target.value }))}
                placeholder="https://…"
              />
              <div style={{ marginTop: 8 }}>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleGalleryUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="module-btn secondary"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={galleryUploading}
                >
                  {galleryUploading ? 'Uploading…' : 'Append uploaded image URL'}
                </button>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>FAQs</label>
              {faqs.map((f, idx) => (
                <div key={idx} style={blockStyle}>
                  <input
                    value={f.q}
                    onChange={(e) => {
                      const next = [...faqs];
                      next[idx] = { ...next[idx], q: e.target.value };
                      setFaqs(next);
                    }}
                    placeholder="Question"
                  />
                  <textarea
                    rows={3}
                    value={f.a}
                    onChange={(e) => {
                      const next = [...faqs];
                      next[idx] = { ...next[idx], a: e.target.value };
                      setFaqs(next);
                    }}
                    placeholder="Answer"
                  />
                  <div>
                    <button
                      type="button"
                      className="module-link-btn danger"
                      onClick={() => setFaqs((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" className="module-btn secondary" onClick={() => setFaqs((p) => [...p, emptyFaq()])}>
                Add FAQ
              </button>
            </div>
          </div>

          <div className="legacy-main-actions">
            <button className="module-btn" type="submit" disabled={mutation.isLoading}>
              Save
            </button>
            <button type="button" className="module-btn secondary" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttractionForm;
